import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { ExploreIndexRedirect } from 'src/sections/debug/explore-index-redirect';

import { AuthGuard, MarketingScopeGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

// Overview
const IndexPage = lazy(() => import('src/pages/dashboard'));

// Statistics
const StatisticsPage = lazy(() => import('src/pages/dashboard/statistics'));
const MarketingPage = lazy(() => import('src/pages/dashboard/marketing'));

// Push reminders (KAN-746)
const RemindersPage = lazy(() => import('src/pages/dashboard/reminders'));

// Acquisition (KAN-755)
const AcquisitionPage = lazy(() => import('src/pages/dashboard/acquisition'));

// Ops — the live operations board
const OpsPage = lazy(() => import('src/pages/dashboard/ops'));

// Moderation — published content and the report queue
const ModerationPage = lazy(() => import('src/pages/dashboard/moderation'));

// App User
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserSuggestedPage = lazy(() => import('src/pages/dashboard/user/suggested'));
const UsersWithoutLocationPage = lazy(() => import('src/pages/dashboard/user/without-location'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserDetailPage = lazy(() => import('src/pages/dashboard/user/detail'));

// Admin User
const AdminUserListPage = lazy(() => import('src/pages/dashboard/admin-user/list'));

// Enterprise
const EnterpriseListPage = lazy(() => import('src/pages/dashboard/enterprise/list'));
const EnterpriseCreatePage = lazy(() => import('src/pages/dashboard/enterprise/create'));
const EnterpriseEditPage = lazy(() => import('src/pages/dashboard/enterprise/edit'));

// Location
const LocationMapPage = lazy(() => import('src/pages/dashboard/location/map'));

const LocationListPage = lazy(() => import('src/pages/dashboard/location/list'));
const LocationCreatePage = lazy(() => import('src/pages/dashboard/location/create'));
const LocationEditPage = lazy(() => import('src/pages/dashboard/location/edit'));

// Location Marker
const MarkerListPage = lazy(() => import('src/pages/dashboard/location/marker/list'));
const MarkerCreatePage = lazy(() => import('src/pages/dashboard/location/marker/create'));
const MarkerEditPage = lazy(() => import('src/pages/dashboard/location/marker/edit'));

// Reports
const TrainingReportsPage = lazy(() => import('src/pages/dashboard/reports/training-reports'));
const TrainingReportDetailPage = lazy(
  () => import('src/pages/dashboard/reports/training-report-detail')
);
const PlaceReportsPage = lazy(() => import('src/pages/dashboard/reports/place-reports'));
const UserReportsPage = lazy(() => import('src/pages/dashboard/reports/user-reports'));
const UserReportDetailPage = lazy(() => import('src/pages/dashboard/reports/user-report-detail'));

// Support
const SupportTicketsPage = lazy(() => import('src/pages/dashboard/support/tickets'));

// Debug
const DebugExploreMapPage = lazy(() => import('src/pages/dashboard/debug/explore/map'));
const DebugExploreDeckPage = lazy(() => import('src/pages/dashboard/debug/explore/deck'));
const DebugExploreMatchesPage = lazy(() => import('src/pages/dashboard/debug/explore/matches'));

// Feedback
const AccountDeletionFeedbackPage = lazy(
  () => import('src/pages/dashboard/feedback/account-deletion')
);

// Communications (KAN-695)
const CommunicationsListPage = lazy(() => import('src/pages/dashboard/communications/list'));
const CommunicationsCreatePage = lazy(() => import('src/pages/dashboard/communications/create'));
const CommunicationsEditPage = lazy(() => import('src/pages/dashboard/communications/edit'));
const CommunicationsDetailPage = lazy(() => import('src/pages/dashboard/communications/detail'));

// Publicity
const PublicityListPage = lazy(() => import('src/pages/dashboard/publicity/list'));
const PublicityCreatePage = lazy(() => import('src/pages/dashboard/publicity/create'));
const PublicityEditPage = lazy(() => import('src/pages/dashboard/publicity/edit'));
// Website events (KAN-788)
const SiteEventListPage = lazy(() => import('src/pages/dashboard/site-events/list'));
const SiteEventCreatePage = lazy(() => import('src/pages/dashboard/site-events/create'));
const SiteEventEditPage = lazy(() => import('src/pages/dashboard/site-events/edit'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <MarketingScopeGuard>
      <SuspenseOutlet />
    </MarketingScopeGuard>
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { element: <IndexPage />, index: true },
      { path: 'statistics', element: <StatisticsPage /> },
      { path: 'reminders', element: <RemindersPage /> },
      { path: 'marketing', element: <MarketingPage /> },

      { path: 'acquisition', element: <AcquisitionPage /> },
      { path: 'ops', element: <OpsPage /> },
      { path: 'moderation', element: <ModerationPage /> },
      {
        path: 'app-user',
        children: [
          { index: true, path: 'list', element: <UserListPage /> },
          { path: 'suggested', element: <UserSuggestedPage /> },
          { path: 'without-location', element: <UsersWithoutLocationPage /> },
          { path: 'edit/:id', element: <UserEditPage /> },
          { path: 'detail/:id', element: <UserDetailPage /> },
        ],
      },
      {
        path: 'admin-user',
        children: [{ index: true, path: 'list', element: <AdminUserListPage /> }],
      },
      {
        path: 'enterprise',
        children: [
          { index: true, path: 'list', element: <EnterpriseListPage /> },
          { path: 'create', element: <EnterpriseCreatePage /> },
          { path: 'edit/:id', element: <EnterpriseEditPage /> },
        ],
      },
      {
        path: 'location',
        children: [
          { index: true, path: 'map', element: <LocationMapPage /> },

          { path: 'list', element: <LocationListPage /> },
          { path: 'create', element: <LocationCreatePage /> },
          { path: 'edit/:id', element: <LocationEditPage /> },
          {
            path: 'marker',
            children: [
              { index: true, path: 'list', element: <MarkerListPage /> },
              { path: 'create', element: <MarkerCreatePage /> },
              { path: 'edit/:id', element: <MarkerEditPage /> },
            ],
          },
        ],
      },
      {
        path: 'reports',
        children: [
          { index: true, path: 'trainings', element: <TrainingReportsPage /> },
          { path: 'trainings/:id', element: <TrainingReportDetailPage /> },
          { path: 'places', element: <PlaceReportsPage /> },
          { path: 'users', element: <UserReportsPage /> },
          { path: 'users/:id', element: <UserReportDetailPage /> },
        ],
      },
      {
        path: 'feedback',
        children: [
          { index: true, path: 'account-deletion', element: <AccountDeletionFeedbackPage /> },
        ],
      },
      {
        path: 'support',
        children: [{ index: true, path: 'tickets', element: <SupportTicketsPage /> }],
      },
      {
        path: 'debug',
        children: [
          {
            path: 'explore',
            children: [
              { index: true, element: <ExploreIndexRedirect /> },
              { path: 'map', element: <DebugExploreMapPage /> },
              { path: 'deck', element: <DebugExploreDeckPage /> },
              { path: 'matches', element: <DebugExploreMatchesPage /> },
            ],
          },
        ],
      },
      {
        path: 'communications',
        children: [
          { index: true, path: 'list', element: <CommunicationsListPage /> },
          { path: 'create', element: <CommunicationsCreatePage /> },
          { path: 'edit/:id', element: <CommunicationsEditPage /> },
          { path: ':id', element: <CommunicationsDetailPage /> },
        ],
      },
      {
        path: 'publicity',
        children: [
          { index: true, path: 'list', element: <PublicityListPage /> },
          { path: 'create', element: <PublicityCreatePage /> },
          { path: 'edit/:id', element: <PublicityEditPage /> },
        ],
      },
      {
        path: 'site-events',
        children: [
          { index: true, path: 'list', element: <SiteEventListPage /> },
          { path: 'create', element: <SiteEventCreatePage /> },
          { path: 'edit/:id', element: <SiteEventEditPage /> },
        ],
      },
    ],
  },
];
